import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEmpty } from "lodash";
import { Controller, useForm } from "react-hook-form";

import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import fontWeight from "../../../styles/mui/fontWeight";
import colors from "../../../styles/colors/colors";
import { ApplicationTitles, Header, ProviderListing } from "../../../constants/HomePage";
import { SearchTextfield, TableCellRow } from "../../HomeScreen/style/style";
import { ServiceProvider } from "../../../models/ServiceProvider";
import { currentDate, getCurrnetPageFullPath, getUaserPageViewPayload } from "../../../utils";
import { MainContainer, ProviderListingHeader } from "../style/style";
import { ButtonContained } from "../../../app/common/button";
import { ButtonVerifyStyle, ContainerStyle } from "../../../app/styles/styles";
import { SORT_TYPE } from "../../../constants/ServiceProvider";
import { UserPageTrack } from "../../../models/User";
import { getValidMessageType } from "../../../constants/User";
import { redirectUri } from "../../../constants/Config";

export interface PropsFromState {
  masterServiceProviderState: ServiceProvider[];
  serviceProviderList: ServiceProvider[];
}

export interface PropsFromDispatch {
  getMasterServiceProvider: () => void;
  postUserPageTrack: (payload: UserPageTrack) => void;
}

export interface Data {
  searchTerm: string;
}

type AllProps = PropsFromState & PropsFromDispatch;

const PatientList: React.FC<AllProps> = ({
  masterServiceProviderState,
  getMasterServiceProvider,
  serviceProviderList,
  postUserPageTrack
}) => {
  const [groupedItems, setGroupedItems] = useState<any>([]);
  const [providerList, setProviderList] = useState<ServiceProvider[] | undefined>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const methods = useForm<any>();
  
  const { control, getValues, watch, setValue } = methods;

  useEffect(() => {
    const subscription = watch((_value: any) => {
      const filterTypes = SORT_TYPE;
      const isAllEmpty =
        isEmpty(_value?.providerName) &&
        isEmpty(_value?.providerNpi) &&
        isEmpty(_value?.providerState);
      if (isAllEmpty) {
        setDefaultList();
      } else {
        for (const { key, type } of filterTypes) {
          const value = _value?.[key];
          if (!isEmpty(value)) {
            const filteredList = updateFilterby(value || "", type);
            const sortedList = getSortedItems(filteredList);
            const grouped = groupItemsByAlphabet(sortedList);
            setGroupedItems(grouped);
            setProviderList(filteredList);
            break;
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const updateFilterby = (value: string, type: string) => {
      if (isEmpty(value)) {
        setDefaultList();
        return [];
      }
      const search = value.toLowerCase();
      return serviceProviderList.filter((provider: ServiceProvider) => {
        switch (type) {
        case "providerNpi":
          return provider.providerNpi.toLowerCase().includes(search);
        case "providerState":
          return provider.providerState.toLowerCase().includes(search);
        case "providerName":
        default:
          return provider.providerName.toLowerCase().includes(search);
        }
      });
  }

  const setDefaultList = () => {
    setProviderList(masterServiceProviderState);
    setGroupedItems(
      groupItemsByAlphabet(getSortedItems(masterServiceProviderState))
    );
  };

  const groupItemsByAlphabet = (items: ServiceProvider[]) => {
    return items.reduce((groups: any, item: ServiceProvider) => {
      const firstLetter = item.providerName.charAt(0).toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(item);
      return groups;
    }, {});
  };

  const getSortedItems = (list: any) => {
    const sortedItems = [...list].sort((a, b) =>
      a.providerName.localeCompare(b.providerName)
    );
    return sortedItems;
  };

  useEffect(() => {
    setProviderList(masterServiceProviderState);
    setGroupedItems(
      groupItemsByAlphabet(getSortedItems(masterServiceProviderState))
    );
  }, [masterServiceProviderState]);

  useEffect(() => {
    postUserPageTrack(
      getUaserPageViewPayload(
        getValidMessageType(ApplicationTitles.PROVIDER_LIST),
        getCurrnetPageFullPath(),
        redirectUri,
        ""
      )
    );
    getMasterServiceProvider();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setGroupedItems([]);
    }
  };

  const rowCell = (
    value: string | number,
    index: number,
    isTotal?: boolean
  ) => {
    return <TableCell style={TableCellRow(index, isTotal)}>{value}</TableCell>;
  };
  return (
    <Paper>
      <Grid container>
        <Grid size={{ xs: 12 }} pt={2} pl={2}>
          <Typography variant="h1" fontWeight={fontWeight[5]}>
            Provider List
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }} pb={2} pt={2}>
          <Grid container sx={ProviderListingHeader}>
            <Grid p={2}>
              <Typography variant="h3" fontWeight={fontWeight[5]}>
                Total Provider List
              </Typography>
            </Grid>
            <Grid p={2} display="flex" alignItems="center" gap={2}>
                <Controller
                name="providerName"
                control={control}
                render={({ field: controllerField }) => (
                  <TextField
                  {...controllerField}
                  id="outlined-providerName"
                  variant="outlined"
                  size="small"
                  value={getValues("providerName") || ""}
                  sx={SearchTextfield}
                  placeholder="Search by Name"
                  onChange={(e) => {
                    setValue("providerState", "");
                    setValue("providerNpi", "");
                    setValue("providerName", e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                  inputRef={inputRef}
                  />
                )}
                />
                <Typography variant="h4" sx={{ mx: 1 }}>OR</Typography>
                <Controller
                name="providerState"
                control={control}
                render={({ field: controllerField }) => (
                  <TextField
                  {...controllerField}
                  id="outlined-providerState"
                  variant="outlined"
                  size="small"
                  value={getValues("providerState") || ""}
                  sx={SearchTextfield}
                  placeholder="Search by State"
                  onChange={(e) => {
                    setValue("providerName", "");
                    setValue("providerNpi", "");
                    setValue("providerState", e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                  />
                )}
                />
                <Typography variant="h4" sx={{ mx: 1 }}>OR</Typography>
                <Controller
                name="providerNpi"
                control={control}
                render={({ field: controllerField }) => (
                  <TextField
                  {...controllerField}
                  id="outlined-providerNpi"
                  variant="outlined"
                  size="small"
                  value={getValues("providerNpi") || ""}
                  sx={SearchTextfield}
                  placeholder="Search by NPI"
                  onChange={(e) => {
                    setValue("providerName", "");
                    setValue("providerState", "");
                    setValue("providerNpi", e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                  />
                )}
              />
              <ButtonContained
              id="btnSubmitClear"
              onClick={() => {
                ["providerName", "providerState", "providerNpi", "searchTerm"].forEach(f => setValue(f, ""));
                setDefaultList();
                }}
                label="Clear Filters"
                disabled={false}
                style={ButtonVerifyStyle}
              />
            </Grid>
            <Grid p={2}>Total Providers:{providerList?.length ?? 0}</Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid size={{ xs: 12 }} sx={MainContainer}> 
        <Grid size={{ xs: 12 }} pb={"1rem"}>
          <TableContainer component={Paper} sx={ContainerStyle}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {ProviderListing.map((data: Header) => (
                    <TableCell key={data.label}>
                      <Box sx={data.style?.cell}>
                        <Typography
                          color={colors.portalColor[2]}
                          variant="h4"
                          fontWeight={fontWeight[6]}
                        >
                          {data.label}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(groupedItems).map((letter) => (
                  <React.Fragment key={letter}>
                    <TableRow>
                      <Grid size={{ xs: 12 }}>
                        <TableCell align="left" style={{ borderBottom: 0 }}>
                          <Typography
                            variant="h3"
                            fontWeight={fontWeight[6]}
                            color={colors.black[1]}
                          >
                            {letter}
                          </Typography>
                        </TableCell>
                      </Grid>
                    </TableRow>
                    {!isEmpty(letter) &&
                      groupedItems[letter].map(
                        (item: ServiceProvider, index: number) => (
                          <TableRow key={item.serviceProviderId}>
                            {rowCell(item.providerName, index)}
                            {rowCell(item.providerNpi, index)}
                            {rowCell(item.providerState, index)}
                            {rowCell(
                              "In Review", //TBD
                              index
                            )}
                            {
                              rowCell("Yes", index) //TBD
                            }
                            {rowCell(
                              formatDate(currentDate(), DateFormats.DD_MM_YYYY),
                              index
                            )}
                            {rowCell(
                              formatDate(currentDate(), DateFormats.DD_MM_YYYY),
                              index
                            )}
                          </TableRow>
                        )
                      )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Paper>
  );
};
export default PatientList;
